import { composeProgress } from "apollo/utils";
import {
  CLINICAL_CONSULTATION_ACTION,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
  RECARE_FILE_TYPE,
  SEARCH_ACTION_EDIT_SEARCH,
  SEARCH_ACTION_GENERATE_FORM,
  TRACK_EVENTS,
} from "core/consts";
import aiApi from "core/model/api/endpoints/extractDocs";
import { getErrorMessage } from "core/model/utils/errors";
import { activateDocsSaveMetadata } from "core/model/utils/featureFlags";
import {
  Auction,
  AuctionForm,
  ClinicalConsultation,
  DocsFormDataMapped,
  DocsFormName,
  DocsFormStatusResponse,
  EncryptedField,
  GKVGeneralFormData,
  Patient,
  QueryProgress,
} from "core/types";
import { useClinicalConsultationAction } from "dsl/hooks";
import useSearchAction from "dsl/hooks/useSearchAction";
import cloneDeep from "lodash/cloneDeep";
import { useCallback, useState } from "react";
import { convertEncryptedFieldOut } from "react-forms-state";
import { useTracking } from "react-tracking";
import { SourceInformation } from "./useDocsForm";
import {
  buildFormSaveTrackingStats,
  buildSaveFormMetadata,
  FORM_NAME_TO_FILE_TYPE,
  isDischargeLetterFormName,
} from "./utils";

export function useFormSave<T extends DocsFormName>({
  auction,
  auctionForms,
  consultation,
  docsFillFormResult,
  formName,
  patient,
  previousFormData,
  sourceInformation,
}: {
  auction: Auction | undefined;
  auctionForms: AuctionForm[];
  consultation: ClinicalConsultation | undefined;
  docsFillFormResult: DocsFormStatusResponse | null;
  formName: T;
  patient: Patient;
  previousFormData: DocsFormDataMapped<T> | null | undefined;
  sourceInformation: SourceInformation<T>;
}) {
  const { trackEvent } = useTracking();

  const [progress, setProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );

  const [
    generateAuctionForm,
    generateAuctionFormProgress,
    resetProgressAuctionForm,
  ] = useSearchAction({ actionType: SEARCH_ACTION_GENERATE_FORM });

  const [
    generateFormConsultation,
    generateFormConsultationProgress,
    resetProgressConsultation,
  ] = useClinicalConsultationAction({
    actionType: CLINICAL_CONSULTATION_ACTION.GENERATE_FORM,
  });

  const [editSearch] = useSearchAction({
    actionType: SEARCH_ACTION_EDIT_SEARCH,
  });

  const saveForm = useCallback(
    async (
      formData: DocsFormDataMapped<T>,
      isDirty: boolean,
    ): Promise<boolean> => {
      if (!isDirty) return true;

      const fail = () => {
        setProgress(QUERY_PROGRESS_FAILED);
        return false;
      };

      setProgress(QUERY_PROGRESS_PENDING);

      if (!consultation && !auction) {
        console.error("auction or consultation is not defined");
        return fail();
      }

      const fileType = FORM_NAME_TO_FILE_TYPE[formName];

      const isGkv =
        fileType === RECARE_FILE_TYPE.GKV_GENERAL_FORM ||
        fileType === RECARE_FILE_TYPE.GKV_MEDICAL_FORM;

      const metadata = buildSaveFormMetadata({
        auctionForms,
        docsFillFormResult,
        formData,
        formName,
        includeAuctionSchemaVersion: !!auction,
        previousFormData,
        sourceInformation,
      });

      const trackingStats = buildFormSaveTrackingStats({
        docsFillFormResult,
        formData,
        metadata,
      });

      const isMigratedForm = isDischargeLetterFormName(formName);
      const submittedData = isMigratedForm ? { values: formData } : formData;

      const syncValidatedFormToAiCore = async () => {
        const careseekerId = patient.careseeker?.id;
        if (!careseekerId || !activateDocsSaveMetadata) {
          return;
        }
        try {
          await aiApi.docs.saveValidatedForm({
            auctionId: auction?.id,
            careseekerId,
            consultationId: consultation?.id,
            formName,
            patientId: patient.id,
            validatedForm: { form_data: formData, metadata },
          });
        } catch (err) {
          console.error(
            "[useFormSave] Failed to sync validated form to ai-core:",
            getErrorMessage(err),
          );
        }
      };

      if (consultation) {
        const consultationResult = await generateFormConsultation({
          consultation,
          context: {
            clinical_consultation: {
              ...consultation,
              forms: [
                {
                  form_data: convertEncryptedFieldOut(
                    JSON.stringify(submittedData),
                  ) as EncryptedField,
                  form_type: fileType,
                  metadata,
                },
              ],
            },
          },
          onCompleted: () => resetProgressConsultation(),
          patient,
        });
        if (!consultationResult) {
          return fail();
        }
        await syncValidatedFormToAiCore();
        setProgress(QUERY_PROGRESS_SUCCEED);
        trackEvent({
          ai_fields_accepted: trackingStats.aiFieldsAccepted,
          ai_fields_accepted_count: trackingStats.aiFieldsAccepted.length,
          ai_fields_cleared: trackingStats.aiFieldsCleared,
          ai_fields_cleared_count: trackingStats.aiFieldsCleared.length,
          ai_fields_edited: trackingStats.aiFieldsEdited,
          ai_fields_edited_count: trackingStats.aiFieldsEdited.length,
          form_name: formName,
          manual_fields_added: trackingStats.manualFieldsAdded,
          manual_fields_added_count: trackingStats.manualFieldsAdded.length,
          name: TRACK_EVENTS.DOCS_FORM_SAVED,
        });

        return true;
      }

      if (!auction) {
        console.error("auction is not defined");
        return fail();
      }

      if (isGkv) {
        const caseId = (formData as GKVGeneralFormData).case_id;
        if (caseId && auction.patient.external_id !== caseId) {
          const auctionWithUpdatedPatient = cloneDeep(auction);
          auctionWithUpdatedPatient.patient.external_id = caseId;
          const editedAuction = await editSearch({
            auction,
            context: { auction: auctionWithUpdatedPatient },
          });
          if (editedAuction === null) {
            return fail();
          }
        }
      }

      const updatedAuction = {
        ...auction,
        forms: [
          {
            form_data: convertEncryptedFieldOut(JSON.stringify(submittedData)),
            form_type: fileType,
            metadata,
          },
        ],
      } as Auction;

      const savedAuction = await generateAuctionForm({
        auction,
        context: {
          auction: updatedAuction,
          form_type: fileType,
        },
        onCompleted: () => resetProgressAuctionForm(),
        onError: (err) => {
          console.error(`error saving form - ${getErrorMessage(err)}`);
        },
      });

      if (!savedAuction) {
        return fail();
      }

      await syncValidatedFormToAiCore();
      setProgress(QUERY_PROGRESS_SUCCEED);
      trackEvent({
        ai_fields_accepted: trackingStats.aiFieldsAccepted,
        ai_fields_accepted_count: trackingStats.aiFieldsAccepted.length,
        ai_fields_cleared: trackingStats.aiFieldsCleared,
        ai_fields_cleared_count: trackingStats.aiFieldsCleared.length,
        ai_fields_edited: trackingStats.aiFieldsEdited,
        ai_fields_edited_count: trackingStats.aiFieldsEdited.length,
        form_name: formName,
        manual_fields_added: trackingStats.manualFieldsAdded,
        manual_fields_added_count: trackingStats.manualFieldsAdded.length,
        name: TRACK_EVENTS.DOCS_FORM_SAVED,
      });

      return true;
    },
    [
      auction,
      auctionForms,
      consultation,
      docsFillFormResult,
      editSearch,
      formName,
      generateAuctionForm,
      generateFormConsultation,
      patient,
      previousFormData,
      resetProgressAuctionForm,
      resetProgressConsultation,
      sourceInformation,
      trackEvent,
    ],
  );

  return {
    saveForm,
    saveFormProgress: composeProgress([
      progress,
      generateAuctionFormProgress,
      generateFormConsultationProgress,
    ]),
  };
}
